<template>
  <div class="analytics-container">
    <h2 class="page-title">机器人管理</h2>
    <div class="table-section">
      <div class="toolbar">
        <span class="toolbar-title">飞书机器人列表</span>
        <el-button type="primary" @click="openDialog()">新增机器人</el-button>
      </div>
      <el-table :data="botList" border stripe style="width: 100%;">
        <el-table-column prop="key" label="Key" min-width="160" />
        <el-table-column prop="keyword" label="关键词" min-width="120" />
        <el-table-column prop="enabled" label="启用" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.enabled ? 'success' : 'info'">{{ scope.row.enabled ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Webhook" min-width="280">
          <template #default="scope">
            <span class="ellipsis">{{ scope.row.webhook }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="form.id ? '编辑机器人' : '新增机器人'"
      width="720px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="120px">
        <el-form-item label="Key" required>
          <el-input
            v-model="form.key"
            placeholder="AdSense 同步固定为 adsense_sync"
            :disabled="!!form.id"
          />
        </el-form-item>
        <el-form-item label="Webhook" required>
          <el-input v-model="form.webhook" placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/xxx" />
        </el-form-item>
        <el-form-item label="关键词">
          <el-input v-model="form.keyword" placeholder="发送时自动拼接到消息前，例如：消息：" />
        </el-form-item>
        <el-form-item label="签名 Secret">
          <el-input v-model="form.secret" placeholder="可选，开启签名校验时填写" show-password />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveBot">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { FeishuBotApi, type FeishuBotItem, type FeishuBotPayload } from '../../api/feishuBotApi'

const botList = ref<FeishuBotItem[]>([])
const dialogVisible = ref(false)
const saving = ref(false)
const form = ref({
  id: '',
  key: '',
  webhook: '',
  keyword: '',
  secret: '',
  enabled: true,
})

async function loadList() {
  botList.value = await FeishuBotApi.getList()
}

function openDialog(row?: FeishuBotItem) {
  if (row) {
    form.value = {
      id: row.id,
      key: row.key,
      webhook: row.webhook,
      keyword: row.keyword || '',
      secret: row.secret || '',
      enabled: row.enabled,
    }
  } else {
    form.value = {
      id: '',
      key: '',
      webhook: '',
      keyword: '消息：',
      secret: '',
      enabled: true,
    }
  }
  dialogVisible.value = true
}

async function saveBot() {
  const key = form.value.key.trim()
  const webhook = form.value.webhook.trim()
  if (!key) {
    ElMessage.warning('请填写 Key')
    return
  }
  if (!webhook) {
    ElMessage.warning('请填写 Webhook')
    return
  }
  const payload: FeishuBotPayload = {
    key,
    webhook,
    keyword: form.value.keyword.trim(),
    secret: form.value.secret.trim(),
    enabled: form.value.enabled,
  }
  saving.value = true
  try {
    if (form.value.id) {
      await FeishuBotApi.update(form.value.id, payload)
      ElMessage.success('已更新')
    } else {
      await FeishuBotApi.create(payload)
      ElMessage.success('已创建')
    }
    dialogVisible.value = false
    await loadList()
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.message || error?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function handleDelete(id: string) {
  try {
    await ElMessageBox.confirm('确定删除该机器人？', '提示', { type: 'warning' })
    await FeishuBotApi.delete(id)
    ElMessage.success('已删除')
    await loadList()
  } catch (error: any) {
    if (error !== 'cancel') {
      ElMessage.error(error?.response?.data?.message || error?.message || '删除失败')
    }
  }
}

onMounted(() => {
  loadList().catch((error: any) => {
    ElMessage.error(error?.response?.data?.message || error?.message || '加载失败')
  })
})
</script>

<style scoped>
.analytics-container {
  padding: 8px;
}
.page-title {
  margin: 0 0 16px;
  font-size: 20px;
}
.table-section {
  background: var(--el-bg-color);
  padding: 16px;
  border-radius: 8px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar-title {
  font-weight: 600;
}
.ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
